import './HeadlineList.css';
import './PostView.css';
import { forwardRef, useRef, useImperativeHandle, useContext } from 'react';
import ArrowRightIcon from './ArrowRightIcon';
import HeadlineRenderer, { HeadlineRendererHandle } from './HeadlineRenderer';
import { AppState } from '../context/general';
import { useHeadlineListFocus } from './HeadlineListFocus';

interface HeadlineListProps {
  posts: [string, number][];
  startAt: number;
  onSelect: (feedId: string, postIndex: number) => void;
  onAllRead: () => void;
  onMark?: (feedId: string, postIndex: number, type: 'read', value: boolean) => void;
  onArticleSelect?: () => void;
  isMobile?: boolean;
  isLoading?: boolean;
  showUnreadOnly?: boolean;
  onEndOfUnread?: () => void;
  onShowAll?: () => void;
  nextUnreadFeed?: { id: string; name: string; isFolder?: boolean };
  onNavigateToFeed?: (feedId: string) => void;
  overflowMessage?: string;
  onOverflowAction?: () => void;
  onLoadMore?: () => void;
  loadMoreLabel?: string;
  // True for browse-only (showRead='a') truncated feeds. The headline column shows the
  // end-of-list "Next Feed" button (as in unread-only mode) instead of a "Show more"
  // button, so onLoadMore is suppressed for the renderer in this case.
  loadMoreWithNextFeed?: boolean;
  onKeepAllUnread?: () => void;
  isSavedList?: boolean;
  isUnreadList?: boolean;
}

export interface HeadlineListHandle {
  selectPost: (feedId: string, postIndex: number) => void;
  updatePostStatus: (feedId: string, postIndex: number, type: 'star' | 'readLater' | 'read', value: boolean) => void;
  markAllRead: () => void;
  markAllUnread: () => void;
  keepAllUnread: () => void;
  markOlderThanRead: (days: number) => void;
}

const HeadlineList = forwardRef<HeadlineListHandle, HeadlineListProps>(({
  posts, startAt, onSelect, onAllRead, onMark,
  onArticleSelect, isMobile, isLoading = false,
  showUnreadOnly, onEndOfUnread, onShowAll, nextUnreadFeed, onNavigateToFeed,
  overflowMessage, onOverflowAction, onLoadMore, loadMoreLabel, loadMoreWithNextFeed, onKeepAllUnread, isSavedList, isUnreadList
}, ref) => {
  const appState = useContext(AppState);
  const rendererRef = useRef<HeadlineRendererHandle>(null);
  const headlineListRef = useRef<HTMLDivElement>(null);
  useHeadlineListFocus(headlineListRef);

  useImperativeHandle(ref, () => ({
    selectPost: (feedId, postIndex) => rendererRef.current?.selectPost(feedId, postIndex),
    updatePostStatus: (feedId, postIndex, type, value) => rendererRef.current?.updatePostStatus(feedId, postIndex, type, value),
    markAllRead: () => rendererRef.current?.markAllRead(),
    markAllUnread: () => rendererRef.current?.markAllUnread(),
    keepAllUnread: () => rendererRef.current?.keepAllUnread(),
    markOlderThanRead: (days) => rendererRef.current?.markOlderThanRead(days),
  }), []);

  const handleSelect = (feedId: string, postIndex: number) => {
    onSelect(feedId, postIndex);
    onArticleSelect?.();
  };

  // Don't render HeadlineList in mobile mode - MainLayout will skip to PostView
  if (isMobile) {
    return null;
  }

  return (
    <div className="headlineList flowForm" ref={headlineListRef}>
      {/* Spacer: the PostView's header spans this column too (.unifiedHeader). */}
      <div className="headlineListHeaderSpace" />
      {isLoading ? (
        <div className="headlineItems">
          <div style={{ padding: '20px', textAlign: 'center', color: 'var(--gray-500)' }}>
            Loading posts...
          </div>
        </div>
      ) : !posts.length ? (
        <div className="headlineItems">
          <div style={{ padding: '20px', textAlign: 'center', color: 'var(--gray-500)' }}>
            {showUnreadOnly ? 'All posts have been read' : 'No posts to display'}
          </div>
          {showUnreadOnly && nextUnreadFeed && (
            <div className="headlineNextFeed">
              <div className="endOfUnreadNextFeed">
                <button className="markAllReadBottomButton flowFocussable" onClick={() => onNavigateToFeed?.(nextUnreadFeed.id)}>
                  <ArrowRightIcon size={14} />
                  <span style={{ marginLeft: 6 }}>Next Feed</span>
                </button>
                <span className="endOfUnreadNextFeedName">{nextUnreadFeed.name}</span>
              </div>
            </div>
          )}
        </div>
      ) : (
        <HeadlineRenderer
          ref={rendererRef}
          posts={posts}
          startAt={startAt}
          onAllRead={onAllRead}
          onMark={onMark}
          onSelect={handleSelect}
          showUnreadOnly={showUnreadOnly}
          onEndOfUnread={onEndOfUnread}
          onShowAll={onShowAll}
          overflowMessage={overflowMessage}
          onOverflowAction={onOverflowAction}
          onLoadMore={loadMoreWithNextFeed ? undefined : onLoadMore}
          loadMoreLabel={loadMoreLabel}
          loadMoreWithNextFeed={loadMoreWithNextFeed}
          userPreferences={appState.state.userPreferences}
          nextUnreadFeed={nextUnreadFeed}
          onNavigateToFeed={onNavigateToFeed}
          onKeepAllUnread={() => { rendererRef.current?.keepAllUnread(); onKeepAllUnread?.(); }}
          isSavedList={isSavedList}
          isUnreadList={isUnreadList}
        />
      )}
    </div>
  );
});

export default HeadlineList;
